import {
  CLOSED_DAY_NOTE_MAX,
  CLOSED_DAYS_MAX,
  SHIP_BY_BASES,
  SHIP_BY_BASIS_LABELS,
  WEEKDAY_SHORT_LABELS,
  closedDayGroupText,
  closedDayGroups,
  halfDaysText,
  isCalendarDay,
  noHolidayToAddText,
  normalShipByRule,
  shipByChangeAuthor,
  shipByDefinitionText,
  shipByImpactText,
  shipByRuleChanges,
  shipByRuleProblem,
  turkishHolidaysCoverText,
  withTurkishHolidays,
  type ClosedDay,
  type ClosedDayGroup,
  type ShipByBasis,
  type ShipByDefinition,
  type ShipByImpact,
  type ShipByRule,
  type ShipByRuleSaved,
  type TurkishHolidayAddition,
} from '@wms/domain';
import { useState } from 'react';
import { api, errorMessage } from '../../api';
import { fmt } from '../../format';
import { addDays } from '../../performance';
import { Modal, useToast } from '../../ui';
import { Icon } from '../../ui/icons';

/** A bayram added at once: the days from its first to its last */
const CLOSED_RANGE_MAX = 31;

const BASIS_HINTS: Readonly<Record<ShipByBasis, string>> = {
  marketplace: 'Süre veren pazaryerlerinin (Trendyol, Hepsiburada…) siparişlerinde onların son kargo zamanı; diğerlerinde depo kuralı. Şimdiye kadarki işleyiş.',
  warehouse: 'Her siparişte depo kuralı; pazaryerinin verdiği süreye bakılmaz (süre kayıtta kalır, sonra yine seçilebilir).',
  earliest: 'Pazaryerinin süresi ile depo kuralından hangisi önce dolarsa: ikisine de yetişen sipariş zamanında sayılır.',
};

/**
 * Under "Zamanında kargo": the definition in plain words, generated from the warehouse's settings (each
 * warehouse's when a seller works in several), with its last change; who made it for the warehouse side.
 */
export function ShipByDefinitionNote({ definitions, staff }: { definitions: readonly ShipByDefinition[]; staff: boolean }) {
  const today = fmt.today();
  return (
    <span className="shipby-def">
      {definitions.map((d) => (
        <span key={d.warehouseId} className="shipby-def-one">
          {definitions.length > 1 && <b>{d.warehouseCode} deposu</b>}
          <span>{shipByDefinitionText(d.rule, d.channels, today).join(' ')}</span>
          {d.timezone !== 'Europe/Istanbul' && <span>Gün ve saatler deponun saat dilimine göre ({d.timezone}).</span>}
          {d.lastChange && (
            <span className="shipby-def-change">
              Son değişiklik: {fmt.stamp(d.lastChange.at)}
              {staff && shipByChangeAuthor(d.lastChange) ? ` · ${shipByChangeAuthor(d.lastChange)}` : ''}
              {d.lastChange.appliedToHistory ? ' · geçmiş siparişlere de uygulandı' : ' · o tarihten önce kapanan siparişler o zamanki tanımla sayılır'}
            </span>
          )}
        </span>
      ))}
    </span>
  );
}

/**
 * "Tanımı değiştir" (depo yöneticisi): cut-off, son kargo saati, working days, closed days and whose deadline
 * a marketplace order has; then a confirmation of what changes before anything is saved.
 */
export function ShipByDialog({ definition, onClose, onSaved }: { definition: ShipByDefinition; onClose: () => void; onSaved: (saved: ShipByRuleSaved) => void }) {
  const toast = useToast();
  const [draft, setDraft] = useState<ShipByRule>(definition.rule);
  const [history, setHistory] = useState(false);
  const [impact, setImpact] = useState<ShipByImpact | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const today = fmt.today();
  const rule = normalShipByRule(draft);
  const problem = shipByRuleProblem(rule);
  const changes = shipByRuleChanges(definition.rule, rule);
  const set = (over: Partial<ShipByRule>) => {
    setDraft({ ...draft, ...over });
    setError(null);
  };
  const isOn = (day: number) => draft.workdays.includes(day);
  const toggle = (day: number) => set({ workdays: isOn(day) ? draft.workdays.filter((d) => d !== day) : [...draft.workdays, day] });

  const review = async () => {
    setBusy(true);
    setError(null);
    try {
      setImpact(await api<ShipByImpact>('POST', '/api/performance/ship-by-rule/preview', { warehouseId: definition.warehouseId, rule }));
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const save = async () => {
    setBusy(true);
    try {
      const saved = await api<ShipByRuleSaved>('PUT', '/api/performance/ship-by-rule', { warehouseId: definition.warehouseId, rule, applyToHistory: history });
      toast(
        saved.appliedToHistory ? 'Tanım kaydedildi; açık ve kapalı siparişler yeni tanımla hesaplandı' : 'Tanım kaydedildi; açık siparişler yeni tanımla hesaplandı',
        'ok',
      );
      onSaved(saved);
    } catch (e) {
      toast(errorMessage(e), 'bad');
      setBusy(false);
    }
  };

  if (impact) {
    return (
      <Modal
        title="Tanımı değiştir: onay"
        onClose={onClose}
        footer={
          <>
            <button className="btn" disabled={busy} onClick={() => setImpact(null)}>
              Geri
            </button>
            <button className="btn primary" disabled={busy} onClick={() => void save()}>
              Evet, kaydet
            </button>
          </>
        }
      >
        <div className="stack shipby-dialog">
          <div className="field">
            <label>Değişenler</label>
            {changes.length > 0 ? (
              <ul className="shipby-list">
                {changes.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
            ) : (
              <span className="muted">Ayarlar aynı kalıyor; yalnız siparişlerin son kargo zamanı yeniden hesaplanıyor.</span>
            )}
          </div>
          <div className="field">
            <label>Siparişlere etkisi</label>
            <ul className="shipby-list">
              {shipByImpactText(impact, history).map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>
            Kimin, ne zaman değiştirdiği kaydedilir ve kartta “Son değişiklik” olarak görünür. Satıcılar yeni tanımı kendi Performans sayfalarında görür.
          </p>
        </div>
      </Modal>
    );
  }

  const past = closedDayGroups(rule.closedDays.filter((d) => d.date < today));
  const ahead = closedDayGroups(rule.closedDays.filter((d) => d.date >= today));
  return (
    <Modal
      title="Zamanında kargo tanımı"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Vazgeç
          </button>
          <button className="btn primary" disabled={busy || problem !== null || (changes.length === 0 && !history)} onClick={() => void review()}>
            Devam
          </button>
        </>
      }
    >
      <div className="stack shipby-dialog">
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          Sipariş son kargo zamanına kadar kargoya verildiyse zamanında sayılır. Son kargo zamanını aşağıdaki ayarlar belirler{definition.timezone !== 'Europe/Istanbul' ? ` (saatler ${definition.timezone})` : ''}.
        </p>
        <div className="form-grid">
          <div className="field">
            <label htmlFor="shipby-cutoff">Kesim saati</label>
            <input id="shipby-cutoff" className="input" type="time" value={draft.cutoff} onChange={(e) => set({ cutoff: e.target.value })} />
            <span className="hint">Bu saate kadar gelen sipariş aynı gün kargoya verilir.</span>
          </div>
          <div className="field">
            <label htmlFor="shipby-deadline">Son kargo saati</label>
            <input id="shipby-deadline" className="input" type="time" value={draft.deadline} onChange={(e) => set({ deadline: e.target.value })} />
            <span className="hint">Kargonun o gün en geç verileceği saat; kurye 17:00’de alıyorsa 17:00, gün sonu için 23:59.</span>
          </div>
        </div>

        <div className="field">
          <label>Çalışma günleri</label>
          <div className="row">
            {[1, 2, 3, 4, 5].map((day) => (
              <button key={day} type="button" className={`chip select${isOn(day) ? ' on' : ''}`} aria-pressed={isOn(day)} onClick={() => toggle(day)}>
                {WEEKDAY_SHORT_LABELS[day - 1]}
              </button>
            ))}
          </div>
          <div className="choice-list shipby-weekend">
            <label className={`choice${isOn(6) ? ' on' : ''}`}>
              <input type="checkbox" checked={isOn(6)} onChange={() => toggle(6)} />
              <span>
                <b>Cumartesi çalışılır</b>
                <small>{isOn(6) ? 'Cumartesi kargo çıkar: cuma kesimden sonra gelen sipariş cumartesi verilir.' : 'Cumartesi kargo çıkmaz: cuma kesimden sonra gelen sipariş sonraki çalışma gününe kalır.'}</small>
              </span>
            </label>
            <label className={`choice${isOn(7) ? ' on' : ''}`}>
              <input type="checkbox" checked={isOn(7)} onChange={() => toggle(7)} />
              <span>
                <b>Pazar çalışılır</b>
                <small>{isOn(7) ? 'Pazar kargo çıkar.' : 'Pazar kargo çıkmaz.'}</small>
              </span>
            </label>
          </div>
        </div>

        <ClosedDays
          ahead={ahead}
          past={past}
          closedDays={rule.closedDays}
          today={today}
          onRemove={(g) => set({ closedDays: draft.closedDays.filter((d) => d.date < g.from || d.date > g.to) })}
          onAdd={(dates, note) =>
            set({ closedDays: [...draft.closedDays.filter((d) => !dates.includes(d.date)), ...dates.map((date) => ({ date, note }))] })
          }
          onHolidays={(closedDays) => set({ closedDays })}
        />

        <div className="field">
          <label>Pazaryeri siparişlerinde esas</label>
          <div className="choice-list">
            {SHIP_BY_BASES.map((basis) => (
              <label key={basis} className={`choice${draft.basis === basis ? ' on' : ''}`}>
                <input type="radio" name="shipby-basis" checked={draft.basis === basis} onChange={() => set({ basis })} />
                <span>
                  <b>{SHIP_BY_BASIS_LABELS[basis]}</b>
                  <small>{BASIS_HINTS[basis]}</small>
                </span>
              </label>
            ))}
          </div>
        </div>

        {problem ? (
          <span className="hint bad">{problem}</span>
        ) : (
          <div className="banner info-soft shipby-preview">
            <b>{changes.length > 0 ? 'Yeni tanım' : 'Tanım'}</b>
            <span>{shipByDefinitionText(rule, definition.channels, today).join(' ')}</span>
          </div>
        )}

        <label className={`choice${history ? ' on' : ''}`}>
          <input type="checkbox" checked={history} onChange={(e) => setHistory(e.target.checked)} />
          <span>
            <b>Geçmiş siparişlere de uygula</b>
            <small>
              Kargoya verilmiş, teslim edilmiş, iptal ve iade siparişlerin son kargo zamanı da yeniden hesaplanır; geçmiş Performans rakamları yeni tanıma göre
              değişir. İşaretlemezseniz açık siparişler hemen yeni tanıma geçer, kapanmış olanlar eski tanımla kalır.
            </small>
          </span>
        </label>
        {error && <div className="banner bad">{error}</div>}
      </div>
    </Modal>
  );
}

/**
 * Kapalı günler: the ones to come, each stretch removable; one day or a stretch (a bayram) added with a
 * note, or Türkiye's official holidays at once ("Resmî tatilleri ekle": what it adds is shown first, then
 * the definition is saved as any other change, through Devam and the confirmation).
 */
function ClosedDays(props: {
  ahead: readonly ClosedDayGroup[];
  past: readonly ClosedDayGroup[];
  closedDays: readonly ClosedDay[];
  today: string;
  onRemove: (g: ClosedDayGroup) => void;
  onAdd: (dates: string[], note: string | null) => void;
  onHolidays: (closedDays: ClosedDay[]) => void;
}) {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [note, setNote] = useState('');
  const [showPast, setShowPast] = useState(false);
  // "Resmî tatilleri ekle" open: what it adds to the closed days as they are now
  const [reviewing, setReviewing] = useState(false);
  const [holidaysAdded, setHolidaysAdded] = useState(0);
  const holidays = reviewing ? withTurkishHolidays(props.closedDays, props.today) : null;
  const last = to || from;
  const days = isCalendarDay(from) && isCalendarDay(last) && last >= from ? Math.round((Date.parse(last) - Date.parse(from)) / 86_400_000) + 1 : 0;
  const tooLong = days > CLOSED_RANGE_MAX;
  const add = () => {
    const dates = Array.from({ length: days }, (_, i) => addDays(from, i));
    props.onAdd(dates, note.trim() || null);
    setFrom('');
    setTo('');
    setNote('');
  };
  const row = (g: ClosedDayGroup) => (
    <div key={g.from} className="shipby-closed-row">
      <span>{closedDayGroupText(g)}</span>
      <button type="button" className="btn sm ghost" onClick={() => props.onRemove(g)} aria-label={`${closedDayGroupText(g)} kaldır`}>
        <Icon name="trash" size={16} /> Kaldır
      </button>
    </div>
  );
  return (
    <div className="field">
      <label>Kapalı günler</label>
      <span className="hint">Bayram, resmî tatil gibi kargo çıkmayan günler; girilmeyen gün çalışma günü sayılır. “Resmî tatilleri ekle” bugünden sonraki resmî tatilleri birlikte ekler.</span>
      {holidays ? (
        <HolidayReview
          addition={holidays}
          today={props.today}
          onCancel={() => setReviewing(false)}
          onAdd={() => {
            props.onHolidays([...holidays.closedDays]);
            setHolidaysAdded(holidays.added.length);
            setReviewing(false);
          }}
        />
      ) : (
        <button
          type="button"
          className="btn sm"
          style={{ alignSelf: 'flex-start' }}
          onClick={() => {
            setReviewing(true);
            setHolidaysAdded(0);
          }}
        >
          <Icon name="plus" size={16} /> Resmî tatilleri ekle
        </button>
      )}
      {holidaysAdded > 0 && !holidays && <span className="hint">Resmî tatiller eklendi ({holidaysAdded} gün); kaydetmek için “Devam”.</span>}
      {props.ahead.length > 0 ? <div className="shipby-closed">{props.ahead.map(row)}</div> : <span className="hint">İleride kapalı gün girilmemiş.</span>}
      <div className="shipby-add">
        <div className="field">
          <label htmlFor="shipby-from">Gün</label>
          <input id="shipby-from" className="input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="shipby-to">Son gün (birden çok gün)</label>
          <input id="shipby-to" className="input" type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="shipby-note">Not</label>
          <input id="shipby-note" className="input" value={note} maxLength={CLOSED_DAY_NOTE_MAX} placeholder="Kurban Bayramı" onChange={(e) => setNote(e.target.value)} />
        </div>
        <button type="button" className="btn" disabled={days === 0 || tooLong} onClick={add}>
          <Icon name="plus" size={16} /> Ekle
        </button>
      </div>
      {tooLong && <span className="hint bad">Bir seferde en fazla {CLOSED_RANGE_MAX} gün eklenir.</span>}
      {props.past.length > 0 && (
        <>
          <button type="button" className="btn sm ghost" style={{ alignSelf: 'flex-start' }} onClick={() => setShowPast(!showPast)}>
            Geçmiş kapalı günler ({props.past.length}) {showPast ? 'gizle' : 'göster'}
          </button>
          {showPast && <div className="shipby-closed">{props.past.map(row)}</div>}
        </>
      )}
    </div>
  );
}

/** What "Resmî tatilleri ekle" would add, and what it leaves alone, before the draft changes. */
function HolidayReview({ addition, today, onCancel, onAdd }: { addition: TurkishHolidayAddition; today: string; onCancel: () => void; onAdd: () => void }) {
  const groups = (days: readonly ClosedDay[]) => closedDayGroups(days).map(closedDayGroupText);
  return (
    <div className="banner info-soft shipby-preview">
      <b>Resmî tatiller, bugünden sonra</b>
      {addition.added.length > 0 ? (
        <>
          <span>Eklenecek ({addition.added.length} gün):</span>
          <ul className="shipby-list">
            {groups(addition.added).map((g) => (
              <li key={g}>{g}</li>
            ))}
          </ul>
        </>
      ) : (
        <span>
          {addition.overLimit.length > 0 ? `Eklenecek yer yok: en fazla ${CLOSED_DAYS_MAX} kapalı gün girilebilir.` : noHolidayToAddText(addition)}
        </span>
      )}
      {addition.existing.length > 0 && <span>Zaten girilmiş, olduğu gibi kalıyor: {groups(addition.existing).join(', ')}.</span>}
      {addition.overLimit.length > 0 && (
        <span>
          En fazla {CLOSED_DAYS_MAX} kapalı gün girilebildiği için eklenmeyen ({addition.overLimit.length} gün): {groups(addition.overLimit).join(', ')}.
        </span>
      )}
      {addition.halfDays.length > 0 && <span>{halfDaysText(addition.halfDays)}</span>}
      <span className="muted">
        {turkishHolidaysCoverText(today)}
        {addition.added.length > 0 ? ' Kaydetmeden önce siparişlere etkisi gösterilir.' : ''}
      </span>
      <div className="row">
        <button type="button" className="btn sm" onClick={onCancel}>
          Vazgeç
        </button>
        <button type="button" className="btn sm primary" disabled={addition.added.length === 0} onClick={onAdd}>
          <Icon name="plus" size={16} /> Listeye ekle
        </button>
      </div>
    </div>
  );
}
